<section id="career-roadmap" class="py-8 sm:py-14 bg-white/60 border-y border-slate-100 relative">
    <div class="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <!-- Left Side: Text Content -->
        <div class="order-1 lg:order-1">
            <span class="inline-block text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 mb-3">Placement
                Readiness</span>
            <h2 class="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">Check
                your <span class="brand-gradient-text">AI Placement Readiness</span> before you apply</h2>
            <p class="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
                Measure your overall job readiness with an AI-powered score based on your technical skills, mock
                interview performance, resume quality, assessments and career profile. Get personalized insights and
                recommendations to improve before applying to your dream job.
            </p>
            <ul class="mt-6 space-y-3">
                <li class="flex items-center gap-2.5 text-slate-700 text-sm font-semibold">
                    <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    AI-powered overall readiness score
                </li>
                <li class="flex items-center gap-2.5 text-slate-700 text-sm font-semibold">
                    <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Resume, assessment &amp; interview evaluation
                </li>
                <li class="flex items-center gap-2.5 text-slate-700 text-sm font-semibold">
                    <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Personalized improvement recommendations
                </li>
            </ul>
            <button class="btn-readiness group" data-testid="roadmap-cta" routerLink="/login">
                Check My Readiness Score <svg
                    class="w-4 h-4 text-indigo-100 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3">
                    </path>
                </svg>
            </button>
        </div>

        <!-- Right Side: Complex UI Component -->
        <div class="relative order-2 lg:order-2 mt-8 lg:mt-0">
            <div class="pointer-events-none absolute inset-0 -m-6 rounded-[40px] bg-violet-300/20 blur-[80px]"></div>

            <!-- Main Card -->
            <div class="relative rounded-[2rem] bg-white border border-slate-100 p-6 md:p-8 shadow-xl">

                <!-- Top Section -->
                <div class="flex items-center justify-between mb-8 relative">
                    <div class="flex items-center gap-3">
                        <div
                            class="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                            <svg class="w-5 h-5 md:w-6 md:h-6 text-violet-600" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z">
                                </path>
                            </svg>
                        </div>
                        <div>
                            <h3 class="font-bold text-slate-900 text-sm md:text-base">Placement Readiness Score</h3>
                            <p class="text-xs text-slate-500 font-medium">Live evaluation · Updated today</p>
                        </div>
                    </div>
                    <div class="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 rounded-lg">
                        <svg class="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z">
                            </path>
                        </svg>
                        <span class="text-[10px] md:text-xs font-bold text-indigo-600 hidden sm:inline">Powered by
                            AI</span>
                        <span class="text-[10px] md:text-xs font-bold text-indigo-600 sm:hidden">AI</span>
                    </div>
                </div>

                <!-- Node Connection Graphic -->
                <div class="roadmap-graphic">
                    <!-- Connecting line -->
                    <div class="roadmap-connector desktop"></div>
                    <div class="roadmap-connector mobile"></div>

                    <!-- Node 1: Resume Analysis -->
                    <div class="roadmap-node">
                        <div class="roadmap-halo emerald">
                            <div class="roadmap-inner-circle emerald">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                            </div>
                        </div>
                        <span class="roadmap-label">Resume<br>Analysis</span>
                    </div>

                    <!-- Node 2: Academic Profile -->
                    <div class="roadmap-node">
                        <div class="roadmap-halo blue">
                            <div class="roadmap-inner-circle blue">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5z"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
                                </svg>
                            </div>
                        </div>
                        <span class="roadmap-label">Academic<br>Profile</span>
                    </div>

                    <!-- Node 3: AI Assessment -->
                    <div class="roadmap-node">
                        <div class="roadmap-halo emerald">
                            <div class="roadmap-inner-circle emerald">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 13l2 2 4-4"></path>
                                </svg>
                            </div>
                        </div>
                        <span class="roadmap-label">AI<br>Assessment</span>
                    </div>

                    <!-- Node 4: AI Mock Interview -->
                    <div class="roadmap-node">
                        <div class="roadmap-halo violet">
                            <div class="roadmap-inner-circle violet">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                </svg>
                            </div>
                        </div>
                        <span class="roadmap-label">AI Mock<br>Interview</span>
                    </div>

                    <!-- Node 5: Placement Readiness Score -->
                    <div class="roadmap-node">
                        <div class="roadmap-halo violet">
                            <div class="roadmap-inner-circle deep-violet">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" stroke-width="2"></circle>
                                    <circle cx="12" cy="12" r="6" stroke-width="2"></circle>
                                    <circle cx="12" cy="12" r="2" stroke-width="2"></circle>
                                </svg>
                            </div>
                        </div>
                        <span class="roadmap-label">Placement<br>Readiness Score</span>
                    </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 items-center mb-6">
                    <!-- Circular Progress -->
                    <div class="flex flex-col items-center">
                        <div class="relative w-28 h-28 flex items-center justify-center">
                            <svg class="w-full h-full -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="none" stroke="#f1f5f9" stroke-width="12"></circle>
                                <circle cx="50" cy="50" r="40" fill="none" stroke="url(#gradient-chart)"
                                    stroke-width="12" stroke-dasharray="251.2" stroke-dashoffset="251.2"
                                    stroke-linecap="round" class="animate-progress-circle" style="--target-offset: 40;">
                                </circle>
                                <defs>
                                    <linearGradient id="gradient-chart" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stop-color="#8b5cf6"></stop>
                                        <stop offset="100%" stop-color="#ec4899"></stop>
                                    </linearGradient>
                                </defs>
                            </svg>
                            <div class="absolute inset-0 flex flex-col items-center justify-center text-center mt-1">
                                <span class="text-2xl font-bold text-slate-900 leading-none mb-0.5">84%</span>
                                <span class="text-[9px] font-bold text-slate-400 tracking-widest">READY</span>
                            </div>
                        </div>
                        <div
                            class="mt-4 px-2.5 py-1 bg-amber-50 border border-amber-100 rounded-full flex items-center gap-1.5">
                            <svg class="w-3 h-3 text-amber-500 fill-amber-500" viewBox="0 0 20 20">
                                <path
                                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z">
                                </path>
                            </svg>
                            <span class="text-[10px] font-bold text-amber-600">Rising Star - Top 15%</span>
                        </div>
                    </div>

                    <!-- Breakdown -->
                    <div class="w-full">
                        <h4
                            class="text-[10px] font-bold tracking-widest text-slate-400 uppercase mb-3 text-center sm:text-left">
                            Readiness Breakdown</h4>
                        <div class="space-y-2.5 w-full">
                            <div>
                                <div class="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                                    <span>Technical Skills</span><span>92%</span>
                                </div>
                                <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div class="h-full bg-violet-500 rounded-full animate-progress-bar"
                                        style="width: 0; --target-width: 92%"></div>
                                </div>
                            </div>
                            <div>
                                <div class="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                                    <span>Communication</span><span>78%</span>
                                </div>
                                <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div class="h-full bg-cyan-500 rounded-full animate-progress-bar"
                                        style="width: 0; --target-width: 78%"></div>
                                </div>
                            </div>
                            <div>
                                <div class="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                                    <span>Problem Solving</span><span>88%</span>
                                </div>
                                <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div class="h-full bg-pink-500 rounded-full animate-progress-bar"
                                        style="width: 0; --target-width: 88%"></div>
                                </div>
                            </div>
                            <div>
                                <div class="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                                    <span>Confidence</span><span>82%</span>
                                </div>
                                <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div class="h-full bg-orange-500 rounded-full animate-progress-bar"
                                        style="width: 0; --target-width: 82%"></div>
                                </div>
                            </div>
                            <div>
                                <div class="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                                    <span>Resume Quality</span><span>95%</span>
                                </div>
                                <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div class="h-full bg-emerald-500 rounded-full animate-progress-bar"
                                        style="width: 0; --target-width: 95%"></div>
                                </div>
                            </div>
                            <div>
                                <div class="flex justify-between text-[10px] font-bold text-slate-700 mb-1">
                                    <span>Aptitude</span><span>90%</span>
                                </div>
                                <div class="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div class="h-full bg-fuchsia-500 rounded-full animate-progress-bar"
                                        style="width: 0; --target-width: 90%"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Recommendation -->
                <div class="bg-indigo-50/50 rounded-xl p-3 flex gap-3 items-start border border-indigo-50">
                    <div class="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
                        <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z">
                            </path>
                        </svg>
                    </div>
                    <div>
                        <h4 class="text-[9px] font-bold text-indigo-600 tracking-widest uppercase mb-0.5">AI
                            RECOMMENDATION</h4>
                        <p class="text-[11px] text-slate-800 font-medium leading-tight mb-1">Improve <span
                                class="font-bold">communication skills</span> to increase your placement readiness by
                            <span class="text-emerald-600 font-bold">+6%</span>.</p>
                        <p class="text-[10px] text-slate-500">Suggested: Take one HR Mock Interview.</p>
                    </div>
                </div>
            </div>

            <!-- Floating badges -->
            <div
                class="absolute -top-4 -left-4 md:-top-6 md:-left-6 bg-white rounded-xl p-2 md:p-3 shadow-lg shadow-slate-200/50 border border-slate-100 flex items-center gap-2.5 animate-[float_4s_ease-in-out_infinite]">
                <div
                    class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                    <svg class="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                </div>
                <div>
                    <p class="text-[9px] md:text-[11px] font-bold text-slate-900 leading-tight">AI Analysis · Resume</p>
                    <p class="text-[9px] md:text-[11px] font-bold text-emerald-600 leading-tight">Score 95%</p>
                </div>
            </div>

            <div
                class="absolute -top-2 -right-2 md:-top-4 md:-right-8 bg-white rounded-xl p-2 md:p-3 shadow-lg shadow-slate-200/50 border border-slate-100 flex items-center gap-2.5 animate-[float_5s_ease-in-out_infinite_reverse]">
                <div class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-violet-500 flex items-center justify-center shrink-0">
                    <svg class="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z">
                        </path>
                    </svg>
                </div>
                <div>
                    <p class="text-[9px] md:text-[11px] font-bold text-slate-900 leading-tight">Interview Confidence</p>
                    <p class="text-[9px] md:text-[11px] font-bold text-violet-600 leading-tight">Excellent · 92%</p>
                </div>
            </div>

            <div
                class="absolute -bottom-3 left-4 md:-bottom-4 md:left-4 bg-white rounded-xl p-2 md:p-3 shadow-lg shadow-slate-200/50 border border-slate-100 flex items-center gap-2.5 animate-[float_4.5s_ease-in-out_infinite_0.5s]">
                <div class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                    <svg class="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z">
                        </path>
                    </svg>
                </div>
                <div>
                    <p class="text-[9px] md:text-[11px] font-bold text-slate-900 leading-tight">Communication</p>
                    <p class="text-[9px] md:text-[11px] font-bold text-amber-600 leading-tight">+6% Potential</p>
                </div>
            </div>

            <div
                class="absolute -bottom-5 right-2 md:-bottom-6 md:right-4 bg-white rounded-xl p-2 md:p-3 shadow-lg shadow-slate-200/50 border border-slate-100 flex items-center gap-2.5 animate-[float_5.5s_ease-in-out_infinite_reverse]">
                <div class="w-6 h-6 md:w-8 md:h-8 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                    <svg class="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                    </svg>
                </div>
                <div>
                    <p class="text-[9px] md:text-[11px] font-bold text-slate-900 leading-tight">ATS Resume</p>
                    <p class="text-[9px] md:text-[11px] font-bold text-blue-600 leading-tight">96%</p>
                </div>
            </div>
        </div>
    </div>
</section>
